import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function Availability() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [newDate, setNewDate] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get(`/availability/${user.id}`).then(({ data }) => setData(data)).catch(() => {});
  }, [user]);

  if (!data) return <div className="text-muted-foreground">...</div>;

  const setDay = (i, k, v) => setData((p) => {
    const w = [...p.weekly];
    w[i] = { ...w[i], [k]: v };
    return { ...p, weekly: w };
  });

  const addBlocked = () => {
    if (!newDate) return;
    if ((data.blocked_dates || []).includes(newDate)) return;
    setData((p) => ({ ...p, blocked_dates: [...(p.blocked_dates || []), newDate].sort() }));
    setNewDate("");
  };

  const removeBlocked = (d) => setData((p) => ({ ...p, blocked_dates: p.blocked_dates.filter((x) => x !== d) }));

  const save = async () => {
    try {
      await api.put("/availability/me", {
        slot_minutes: data.slot_minutes,
        weekly: data.weekly,
        blocked_dates: data.blocked_dates || [],
      });
      toast.success(t("saved"));
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="availability-page">
      <h1 className="font-display text-3xl">{t("availability")}</h1>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("weekly_schedule")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Label className="min-w-[140px]">{t("slot_duration")}</Label>
            <Select value={String(data.slot_minutes)} onValueChange={(v) => setData((p) => ({ ...p, slot_minutes: parseInt(v) }))}>
              <SelectTrigger className="w-[180px]" data-testid="slot-duration-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[10, 15, 20, 30, 45, 60].map((m) => (
                  <SelectItem key={m} value={String(m)}>{m} {t("minutes")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {data.weekly.map((d, i) => (
            <div key={d.day} className="flex items-center gap-3 flex-wrap" data-testid={`day-${d.day}`}>
              <div className="min-w-[140px] flex items-center gap-2">
                <Switch checked={d.enabled} onCheckedChange={(v) => setDay(i, "enabled", v)} data-testid={`day-${d.day}-toggle`} />
                <span className="font-medium">{t(DAY_KEYS[d.day])}</span>
              </div>
              <Input type="time" value={d.start} disabled={!d.enabled} onChange={(e) => setDay(i, "start", e.target.value)} className="w-32" data-testid={`day-${d.day}-start`} />
              <span className="text-muted-foreground">—</span>
              <Input type="time" value={d.end} disabled={!d.enabled} onChange={(e) => setDay(i, "end", e.target.value)} className="w-32" data-testid={`day-${d.day}-end`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("blocked_dates")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-48" data-testid="block-date-input" />
            <Button variant="outline" onClick={addBlocked} data-testid="block-add-btn"><Plus className="w-4 h-4 mr-1" /> {t("add_blocked")}</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(data.blocked_dates || []).map((d) => (
              <button key={d} onClick={() => removeBlocked(d)} className="px-3 py-1 rounded-full bg-[#F3F1EC] text-sm flex items-center gap-2 hover:bg-secondary/20" data-testid={`block-${d}`}>
                {d} <Trash2 className="w-3 h-3" />
              </button>
            ))}
            {(data.blocked_dates || []).length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-availability-btn">{t("save")}</Button>
    </div>
  );
}
